<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="mb-4">
          <a href="/businesses" class="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            &larr; Back to Businesses
          </a>
        </div>
        <div v-if="business" class="flex items-start justify-between">
          <div class="flex items-start space-x-4">
            <div v-if="business.logo_url" class="flex-shrink-0">
              <img :src="business.logo_url" :alt="business.name" class="h-16 w-16 rounded-lg object-cover">
            </div>
            <div v-else class="flex-shrink-0 h-16 w-16 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <svg class="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {{ business.name }}
              </h1>
              <p v-if="business.category" class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Primary category: {{ business.category }}
              </p>
              <div v-if="business.secondary_categories?.length" class="mt-2 flex flex-wrap gap-2">
                <span
                  v-for="category in business.secondary_categories"
                  :key="category"
                  class="inline-flex rounded-md bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                  {{ category }}
                </span>
              </div>
              <p v-if="business.description" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {{ business.description }}
              </p>
            </div>
          </div>
          <div class="flex items-center space-x-2">
            <button
              @click="openEditForm"
              class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
              Edit
            </button>
            <button
              v-if="!business.archived_at"
              @click="handleArchive"
              class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20">
              Archive
            </button>
            <button
              v-else
              @click="handleUnarchive"
              class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-green-600 dark:text-green-400 bg-white dark:bg-gray-700 hover:bg-green-50 dark:hover:bg-green-900/20">
              Unarchive
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="business" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav class="flex gap-6 -mb-px">
          <button
            @click="activeTab = 'overview'"
            :class="activeTab === 'overview' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'"
            class="py-2 px-1 border-b-2 font-medium text-sm">
            Overview
          </button>
          <button
            @click="activeTab = 'negative_keywords'"
            :class="activeTab === 'negative_keywords' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'"
            class="py-2 px-1 border-b-2 font-medium text-sm">
            Negative Keywords
          </button>
          <button
            @click="activeTab = 'convo_radar'"
            :class="activeTab === 'convo_radar' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'"
            class="py-2 px-1 border-b-2 font-medium text-sm">
            Convo Radar
          </button>
        </nav>
      </div>

      <BusinessNegativeKeywordsTab
        v-if="activeTab === 'negative_keywords'"
        :business-id="businessId"
        :account-id="accountId"
        :csrf-token="csrfToken" />

      <BusinessConvoRadarTab
        v-if="activeTab === 'convo_radar'"
        :business-id="businessId"
        :account-id="accountId"
        :csrf-token="csrfToken" />

      <div v-if="activeTab === 'overview'" class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Contact & Address -->
        <div class="lg:col-span-1 space-y-6">
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Contact Information
            </h3>
            <dl class="space-y-3">
              <div v-if="business.phone_number">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Phone
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ business.phone_number }}
                </dd>
              </div>
              <div v-if="business.email">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Email
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ business.email }}
                </dd>
              </div>
              <div v-if="business.website_url">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Website
                </dt>
                <dd class="text-sm">
                  <a :href="business.website_url" target="_blank" class="text-primary-600 dark:text-primary-400 hover:underline">{{ business.website_url }}</a>
                </dd>
              </div>
            </dl>
          </div>

          <div v-if="business.street_address || business.city" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Address
            </h3>
            <p class="text-sm text-gray-900 dark:text-gray-100">
              <span v-if="business.street_address">{{ business.street_address }}<br></span>
              <span v-if="business.city">{{ business.city }}, </span>
              <span v-if="business.state">{{ business.state }} </span>
              <span v-if="business.zip">{{ business.zip }}</span>
              <span v-if="business.country"><br>{{ business.country }}</span>
            </p>
          </div>

          <div v-if="socialLinks.length > 0" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Social Links
            </h3>
            <div class="space-y-2">
              <a
                v-for="link in socialLinks"
                :key="link.label"
                :href="link.url"
                target="_blank"
                class="flex items-center text-sm text-primary-600 dark:text-primary-400 hover:underline">
                {{ link.label }}
              </a>
            </div>
          </div>

          <!-- Onboarding -->
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Onboarding
            </h3>
            <div v-if="onboardingLoading" class="animate-pulse space-y-2">
              <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
              <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            </div>
            <div v-else-if="onboardingData">
              <div class="flex items-center justify-between mb-3">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="onboardingStatusClasses">
                  {{ onboardingStatusLabel }}
                </span>
                <span class="text-xs text-gray-500 dark:text-gray-400">
                  {{ onboardingData.completion_percentage }}% complete
                </span>
              </div>
              <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mb-3">
                <div
                  class="bg-primary-500 h-1.5 rounded-full transition-all"
                  :style="{ width: (onboardingData.completion_percentage || 0) + '%' }" />
              </div>
              <div v-if="onboardingData.account_lead_name" class="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Lead: {{ onboardingData.account_lead_name }}
              </div>
              <a
                :href="`/businesses/${businessId}/onboarding`"
                class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 w-full justify-center">
                {{ onboardingData.status === 'completed' ? 'View Onboarding' : 'Continue Onboarding' }}
              </a>
            </div>
            <div v-else>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
                No onboarding started yet.
              </p>
              <button
                @click="handleStartOnboarding"
                :disabled="!canStartOnboarding"
                class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                {{ creatingOnboarding ? 'Starting...' : 'Start Onboarding' }}
              </button>
              <p v-if="!canStartOnboarding" class="mt-2 text-xs text-amber-700 dark:text-amber-300">
                {{ onboardingBlockedMessage }}
              </p>
            </div>
          </div>
        </div>

        <!-- Locations & Websites -->
        <div class="lg:col-span-2 space-y-8">
          <!-- Locations -->
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Locations ({{ business.locations_count || 0 }})
                </h3>
                <button
                  @click="openLocationForm"
                  class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
                  Add Location
                </button>
              </div>
              <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Locations are physical offices, service areas, or hybrid markets used for local SEO checks. Step 1 address data is auto-added when possible; add more rows for multi-location clients or city/state-only service areas.
              </p>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Missing phone, website, street address, or Place ID can limit NAP, GBP, and local analysis. Analysis Data shows Complete when required details are present, Limited when details are missing, and Excluded for closed, temporarily closed, or unconfirmed locations.
              </p>
              <div class="mt-3">
                <input
                  v-model="locationSearch"
                  type="text"
                  placeholder="Search locations..."
                  class="w-full sm:w-64 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
              </div>
            </div>
            <div v-if="locationsLoading" class="p-6">
              <div v-for="n in 3" :key="n" class="animate-pulse flex space-x-4 mb-4">
                <div class="flex-1 space-y-2">
                  <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                  <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                </div>
              </div>
            </div>
            <div v-else-if="locations.length > 0">
              <LocationsTable :locations="locations" @view="navigateToLocation" />
            </div>
            <div v-else class="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No locations yet. Add a physical office, service area, or hybrid location above.
            </div>
          </div>

          <!-- Websites -->
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Websites ({{ websites.length }})
                </h3>
                <button
                  @click="openWebsiteForm"
                  class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
                  Add Website
                </button>
              </div>
              <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Websites are the crawlable properties used for sitemap imports, page inventory, audits, and optimization. Step 1 website URL is auto-added when possible; add more rows for related domains or subdomains.
              </p>
            </div>
            <div v-if="websitesLoading" class="p-6">
              <div v-for="n in 2" :key="n" class="animate-pulse flex space-x-4 mb-4">
                <div class="flex-1 space-y-2">
                  <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                  <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                </div>
              </div>
            </div>
            <div v-else-if="websites.length > 0">
              <div v-for="website in websites" :key="website.id" class="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                <div class="px-6 py-4">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <button @click="toggleWebsite(website.id)" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <svg class="h-4 w-4 transition-transform" :class="{ 'rotate-90': expandedWebsites[website.id] }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div>
                        <a :href="`/businesses/${businessId}/websites/${website.id}`" class="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500" @click.stop>
                          {{ website.name }}
                        </a>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                          {{ website.domain }}
                        </p>
                      </div>
                    </div>
                    <div class="flex items-center space-x-3">
                      <span class="text-xs text-gray-500 dark:text-gray-400">{{ website.pages_count || 0 }} pages</span>
                      <button
                        @click="openSitemapImport(website)"
                        class="inline-flex items-center px-2 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                        Import Sitemap
                      </button>
                      <button
                        @click="openWebsiteForm(website)"
                        class="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="expandedWebsites[website.id]">
                  <PagesList
                    :ref="el => { if (el) pagesListRefs[website.id] = el }"
                    :website-id="website.id"
                    @add-page="openPageForm(website)" />
                </div>
              </div>
            </div>
            <div v-else class="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No websites yet. Add the primary crawlable website above.
            </div>
          </div>
        </div>
      </div>
    </div>

    <UiSlidePanel ref="slidePanel" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { hideLoadingSpinner } from '~/javascript/utils/hide_loader.js'
import { getBusiness, archiveBusiness, unarchiveBusiness } from '~/javascript/api/businesses.js'
import { getOnboarding, createOnboarding } from '~/javascript/api/onboardings.js'
import { getBusinessLocations } from '~/javascript/api/locations.js'
import { getBusinessWebsites } from '~/javascript/api/websites.js'
import LocationsTable from '~/javascript/components/dashboard/components/locations_table.vue'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'
import BusinessForm from './form.vue'
import LocationForm from '~/javascript/components/locations/form.vue'
import WebsiteForm from '~/javascript/components/websites/form.vue'
import SitemapImportForm from '~/javascript/components/websites/sitemap_import_form.vue'
import PageForm from '~/javascript/components/websites/page_form.vue'
import PagesList from '~/javascript/components/websites/pages_list.vue'
import BusinessNegativeKeywordsTab from '~/javascript/components/negative_keywords/business_negative_keywords_tab.vue'
import BusinessConvoRadarTab from '~/javascript/components/social_monitors/business_convo_radar_tab.vue'

const props = defineProps({
  businessId: { type: String, required: true },
  businessName: { type: String, default: '' },
  accountId: { type: String, default: '' },
  csrfToken: { type: String, default: '' }
})

const activeTab = ref('overview')
const business = ref(null)
const locations = ref([])
const locationsLoading = ref(true)
const locationSearch = ref('')
let searchDebounce = null
const websites = ref([])
const websitesLoading = ref(true)
const expandedWebsites = ref({})
const pagesListRefs = ref({})
const slidePanel = ref(null)
const onboardingData = ref(null)
const onboardingLoading = ref(true)
const creatingOnboarding = ref(false)

const socialLinks = computed(() => {
  if (!business.value) return []
  const links = []
  const map = {
    facebook_url: 'Facebook',
    instagram_url: 'Instagram',
    twitter_url: 'Twitter',
    youtube_url: 'YouTube',
    linkedin_url: 'LinkedIn',
    tiktok_url: 'TikTok'
  }
  Object.entries(map).forEach(([key, label]) => {
    if (business.value[key]) links.push({ label, url: business.value[key] })
  })
  return links
})

function locationHasRequiredSetup(location) {
  const requiredFields = location.location_type === 'service_area_only' ?
    ['city', 'state'] :
    ['address_line_1', 'city', 'state', 'zip', 'country']

  return requiredFields.every((field) => Boolean(location[field]))
}

const hasWebsiteForOnboarding = computed(() => websites.value.length > 0)

const mainLocationForOnboarding = computed(() => (
  locations.value.find((location) => location.is_primary && location.status === 'active' && locationHasRequiredSetup(location))
))

const canStartOnboarding = computed(() => (
  !creatingOnboarding.value &&
  !locationsLoading.value &&
  !websitesLoading.value &&
  hasWebsiteForOnboarding.value &&
  Boolean(mainLocationForOnboarding.value)
))

const onboardingBlockedMessage = computed(() => {
  if (locationsLoading.value || websitesLoading.value) return 'Checking location and website setup...'
  if (!hasWebsiteForOnboarding.value && !mainLocationForOnboarding.value) {
    return 'Add at least one website and one active main location or service area before starting onboarding.'
  }
  if (!hasWebsiteForOnboarding.value) return 'Add at least one website before starting onboarding.'
  if (!mainLocationForOnboarding.value) {
    return 'Mark one active location or service area as Main and complete its required setup before starting onboarding.'
  }

  return ''
})

const fetchBusiness = async() => {
  try {
    const response = await getBusiness(props.businessId)
    business.value = response.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching business:', error)
  }
}

const fetchLocations = async() => {
  try {
    locationsLoading.value = true
    const params = {}
    if (locationSearch.value.trim()) params.q = locationSearch.value.trim()
    const response = await getBusinessLocations(props.businessId, params)
    locations.value = response.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching locations:', error)
  } finally {
    locationsLoading.value = false
  }
}

watch(locationSearch, () => {
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(fetchLocations, 300)
})

const fetchWebsites = async() => {
  try {
    websitesLoading.value = true
    const response = await getBusinessWebsites(props.businessId)
    websites.value = response.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching websites:', error)
  } finally {
    websitesLoading.value = false
  }
}

const fetchOnboarding = async() => {
  try {
    onboardingLoading.value = true
    const response = await getOnboarding(props.businessId)
    onboardingData.value = response.data
  } catch (e) {
    // 404 means no onboarding exists yet — that's fine
    onboardingData.value = null
  } finally {
    onboardingLoading.value = false
  }
}

const handleStartOnboarding = async() => {
  if (!canStartOnboarding.value) return

  creatingOnboarding.value = true
  try {
    await createOnboarding(props.businessId, {})
    window.location.href = `/businesses/${props.businessId}/onboarding`
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error creating onboarding:', e)
    creatingOnboarding.value = false
  }
}

const onboardingStatusLabel = computed(() => {
  const s = onboardingData.value?.status
  const labels = { draft: 'Draft', sent: 'Sent', in_progress: 'In Progress', completed: 'Completed' }
  return labels[s] || ''
})

const onboardingStatusClasses = computed(() => {
  const s = onboardingData.value?.status
  switch (s) {
    case 'draft': return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    case 'sent': return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
    case 'in_progress': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
    case 'completed': return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  }
})

const toggleWebsite = (websiteId) => {
  expandedWebsites.value[websiteId] = !expandedWebsites.value[websiteId]
}

const navigateToLocation = (location) => {
  window.location.href = `/businesses/${props.businessId}/locations/${location.id}`
}

const onBusinessSaved = () => {
  slidePanel.value.close()
  fetchBusiness()
}

const onLocationSaved = () => {
  slidePanel.value.close()
  fetchBusiness()
  fetchLocations()
}

const onWebsiteSaved = () => {
  slidePanel.value.close()
  fetchWebsites()
}

const onSitemapImported = () => {
  fetchWebsites()
  // Refresh expanded pages lists
  Object.keys(pagesListRefs.value).forEach((id) => {
    pagesListRefs.value[id]?.refresh?.()
  })
}

const onPageSaved = (websiteId) => {
  slidePanel.value.close()
  fetchWebsites()
  pagesListRefs.value[websiteId]?.refresh?.()
}

const openEditForm = () => {
  slidePanel.value.open({
    component: BusinessForm,
    props: { business: business.value },
    events: { saved: onBusinessSaved }
  })
}

const openLocationForm = () => {
  // Prefill the first location from the business so name/address/phone
  // don't have to be entered twice.
  const prefill = business.value && locations.value.length === 0 ? {
    name: business.value.name,
    address_line_1: business.value.street_address,
    city: business.value.city,
    state: business.value.state,
    country: business.value.country,
    zip: business.value.zip,
    phone_number: business.value.phone_number,
    email: business.value.email,
    website_url: business.value.website_url
  } : null

  slidePanel.value.open({
    component: LocationForm,
    props: { businessId: props.businessId, prefill },
    events: { saved: onLocationSaved }
  })
}

const openWebsiteForm = (website = null) => {
  const isEdit = website && website.id
  slidePanel.value.open({
    component: WebsiteForm,
    props: isEdit ?
      { website, businessId: props.businessId } :
      { businessId: props.businessId },
    events: { saved: onWebsiteSaved }
  })
}

const openSitemapImport = (website) => {
  slidePanel.value.open({
    component: SitemapImportForm,
    props: { websiteId: website.id, websiteName: website.name },
    events: { imported: onSitemapImported }
  })
}

const openPageForm = (website) => {
  slidePanel.value.open({
    component: PageForm,
    props: { websiteId: website.id },
    events: { saved: () => onPageSaved(website.id) }
  })
}

const handleArchive = async() => {
  try {
    const response = await archiveBusiness(props.businessId)
    business.value = response.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error archiving business:', error)
  }
}

const handleUnarchive = async() => {
  try {
    const response = await unarchiveBusiness(props.businessId)
    business.value = response.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error unarchiving business:', error)
  }
}

onMounted(() => {
  hideLoadingSpinner()
  fetchBusiness()
  fetchLocations()
  fetchWebsites()
  fetchOnboarding()
})
</script>
